import { useEffect, useState, useCallback } from "react";
import { Check, X, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const filters = [
  { id: "pending", label: "Por aprovar" },
  { id: "approved", label: "Publicados" },
  { id: "rejected", label: "Rejeitados" },
  { id: "all", label: "Todos" },
];
const badge = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  approved: "bg-green-50 text-green-700 border-green-200",
  rejected: "bg-stone-100 text-stone-500 border-stone-200",
};
const label = { pending: "Por aprovar", approved: "Publicado", rejected: "Rejeitado" };

export default function ReviewsManager() {
  const [reviews, setReviews] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/admin/reviews?status=${filter}`).then((r) => { setReviews(r.data); setLoading(false); });
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try { await api.patch(`/admin/reviews/${id}`, { status }); toast.success("Atualizado."); load(); }
    catch { toast.error("Erro ao atualizar."); }
  };
  const remove = async (id) => {
    if (!window.confirm("Remover este testemunho?")) return;
    try { await api.delete(`/admin/reviews/${id}`); toast.success("Removido."); load(); }
    catch { toast.error("Erro ao remover."); }
  };

  return (
    <div data-testid="reviews-manager">
      <h2 className="font-display text-2xl text-stone-900">Testemunhos de clientes</h2>
      <p className="text-sm text-stone-500 mt-1">Aprove os testemunhos para aparecerem no site.</p>

      <div className="flex flex-wrap gap-2 mt-6">
        {filters.map((f) => (
          <button key={f.id} onClick={() => setFilter(f.id)} data-testid={`reviews-filter-${f.id}`}
            className={`rounded-full px-4 py-2 text-sm border transition-colors ${
              filter === f.id ? "bg-rose-800 text-white border-rose-800" : "bg-white border-rose-100 text-stone-600 hover:border-rose-300"
            }`}>
            {f.label}
          </button>
        ))}
      </div>

      <div className="mt-6 space-y-3">
        {loading ? (
          <div className="p-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-rose-700" /></div>
        ) : reviews.length === 0 ? (
          <p className="p-12 text-center text-stone-400 bg-white rounded-2xl border border-rose-100">Sem testemunhos nesta categoria.</p>
        ) : (
          reviews.map((r) => (
            <div key={r.id} className="bg-white rounded-2xl border border-rose-100 p-5" data-testid={`review-row-${r.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-stone-900 font-medium">{r.name}
                    {r.tag && <span className="text-stone-400 font-normal"> · {r.tag}</span>}
                  </p>
                  <p className="text-stone-600 italic mt-2">“{r.text}”</p>
                </div>
                <span className={`shrink-0 text-xs px-3 py-1 rounded-full border ${badge[r.status]}`}>{label[r.status]}</span>
              </div>
              <div className="flex gap-2 mt-4">
                {r.status !== "approved" && (
                  <button onClick={() => setStatus(r.id, "approved")} data-testid={`approve-${r.id}`}
                    className="inline-flex items-center gap-1.5 text-xs rounded-full border border-green-200 text-green-700 px-3 py-1.5 hover:bg-green-50">
                    <Check className="w-3.5 h-3.5" /> Aprovar
                  </button>
                )}
                {r.status !== "rejected" && (
                  <button onClick={() => setStatus(r.id, "rejected")} data-testid={`reject-${r.id}`}
                    className="inline-flex items-center gap-1.5 text-xs rounded-full border border-stone-200 text-stone-500 px-3 py-1.5 hover:bg-stone-50">
                    <X className="w-3.5 h-3.5" /> Rejeitar
                  </button>
                )}
                <button onClick={() => remove(r.id)} data-testid={`delete-review-${r.id}`}
                  className="inline-flex items-center gap-1.5 text-xs rounded-full border border-red-100 text-red-500 px-3 py-1.5 hover:bg-red-50">
                  <Trash2 className="w-3.5 h-3.5" /> Apagar
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
